/**
 * Busca Preditiva - Funcionalidade de busca em tempo real usando Section API
 */
class PredictiveSearch {
  constructor() {
    this.searchInput = document.getElementById('header-search-input');
    this.predictiveSearch = document.getElementById('predictive-search');
    this.searchTimeout = null;
    this.isSearching = false;
    this.currentQuery = '';
    this.abortController = null;
    this.productsSwiper = null;
    
    if (!this.searchInput || !this.predictiveSearch) return;
    this.init();
  }
  
  init() {
    this.bindEvents();
    this.setupClickOutside();
    this.setupResizeListener();
    this.setupBeforeUnloadListener();
    this.setupMobileSearchToggle();
  }
  
  bindEvents() {
    this.searchInput.addEventListener('input', (e) => {
      this.handleSearchInput(e.target.value);
    });
    
    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hidePredictiveSearch();
        this.searchInput.blur();
      }
    });
    
    const closeBtn = this.predictiveSearch.querySelector('[data-action="close-predictive-search"]');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.hidePredictiveSearch();
      });
    }
    
    const searchForm = this.searchInput.closest('form');
    if (searchForm) {
      searchForm.addEventListener('submit', () => {
        this.hidePredictiveSearch();
      });
    }
  }
  
  setupClickOutside() {
    document.addEventListener('click', (e) => {
      const isClickInsideSearchArea = this.searchInput === e.target || this.searchInput.contains(e.target);
      const isClickInsidePredictiveSearch = this.predictiveSearch.contains(e.target);
      if (!isClickInsideSearchArea && !isClickInsidePredictiveSearch) {
        this.hidePredictiveSearch();
      }
    });
  }
  
  setupResizeListener() {
    window.addEventListener('resize', () => {
      if (window.innerWidth > 768) this.releaseScroll();
    });
  }
  
  releaseScroll() {
    document.body.classList.remove('predictive-search-active');
    Object.assign(document.body.style, {
      overflow: '', position: '', top: '', left: '', right: '', bottom: '', width: ''
    });
  }
  
  setupBeforeUnloadListener() {
    window.addEventListener('beforeunload', () => this.releaseScroll());
  }
  
  setupMobileSearchToggle() {
    const mobileSearchToggle = document.querySelector('[data-action="toggle-search-mobile"]');
    const searchWrapper = document.querySelector('.header-search-wrapper');
    if (mobileSearchToggle && searchWrapper) {
      mobileSearchToggle.addEventListener('click', () => {
        searchWrapper.classList.toggle('search-active');
        if (searchWrapper.classList.contains('search-active')) {
          setTimeout(() => this.searchInput.focus(), 100);
        }
      });
    }
  }
  
  handleSearchInput(query) {
    const trimmedQuery = query.trim();
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    
    if (trimmedQuery.length === 0) {
      this.hidePredictiveSearch();
      return;
    }
    
    this.showPredictiveSearch();
    this.searchTimeout = setTimeout(() => this.performSearch(trimmedQuery), 500);
  }
  
  async performSearch(query) {
    if (this.abortController) this.abortController.abort();
    if (this.isSearching) return;
    
    this.isSearching = true;
    this.currentQuery = query;
    this.abortController = new AbortController();
    this.showLoading();
    
    try {
      const htmlResults = await this.fetchSearchResults(query, this.abortController.signal);
      this.displayResults(htmlResults, query);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Erro na busca preditiva:', error);
        this.showError();
      }
    } finally {
      this.isSearching = false;
      this.abortController = null;
    }
  }
  
  async fetchSearchResults(query, signal = null) {
    const searchParams = new URLSearchParams({
      'q': query,
      'section_id': 'predictive-search'
    });
    const searchUrl = `${window.Shopify.routes.root}search/suggest?${searchParams.toString()}`;
    const response = await fetch(searchUrl, { signal });
    if (!response.ok) throw new Error('Erro na requisição');
    return await response.text();
  }
  
  displayResults(html, query) {
    this.hideAllStates();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const resultsHTML = doc.getElementById('predictive-search-results');
    
    if (!resultsHTML || resultsHTML.innerHTML.trim() === '') {
      this.showNoResults(query);
      return;
    }
    
    const resultsContainer = this.predictiveSearch.querySelector('.predictive-search-results');
    resultsContainer.innerHTML = resultsHTML.innerHTML;
    resultsContainer.style.display = 'flex';
    
    this.bindSuggestionButtons(resultsContainer);
    this.initProductsSwiper();
    this.checkSidebarContent(resultsContainer);
  }

  checkSidebarContent(resultsContainer) {
    const sidebar = resultsContainer.querySelector('.predictive-search-sidebar');
    if (!sidebar) return;
    const sidebarSections = sidebar.querySelectorAll('.predictive-search-section');
    let hasContent = false;
    sidebarSections.forEach(section => {
      if (section.querySelectorAll('.predictive-search-item, .predictive-search-query').length > 0) hasContent = true;
    });
    resultsContainer.classList.toggle('sidebar-empty', !hasContent);
  }

  showLoading() {
    this.hideAllStates();
    const loading = this.predictiveSearch.querySelector('.predictive-search-loading');
    if (loading) loading.style.display = 'flex';
  }
  
  showNoResults(query) {
    this.hideAllStates();
    const noResults = this.predictiveSearch.querySelector('.predictive-search-no-results');
    if (noResults) {
        const qElem = noResults.querySelector('.search-query');
        if (qElem) qElem.textContent = query;
        noResults.style.display = 'block';
    }
  }
  
  showError() {
    this.hideAllStates();
    const error = this.predictiveSearch.querySelector('.predictive-search-error');
    if (error) error.style.display = 'block';
  }
  
  hideAllStates() {
    const states = ['.predictive-search-loading', '.predictive-search-results', '.predictive-search-no-results', '.predictive-search-error'];
    states.forEach(selector => {
      const element = this.predictiveSearch.querySelector(selector);
      if (element) element.style.display = 'none';
    });
  }
  
  showPredictiveSearch() {
    this.predictiveSearch.style.display = 'flex';
    if (window.innerWidth <= 768) {
      document.body.classList.add('predictive-search-active');
      Object.assign(document.body.style, {
        overflow: 'hidden', position: 'fixed', top: '0', left: '0', right: '0', bottom: '0', width: '100%'
      });
    }
    if (!this.searchInput.value || this.searchInput.value.trim().length === 0) {
      this.performInitialSearch();
    }
  }
  
  async performInitialSearch() {
    try {
      const collectionHandle = this.predictiveSearch.getAttribute('data-initial-collection-handle');
      if (collectionHandle) {
        const response = await fetch(`/collections/${collectionHandle}/products.json?limit=10`);
        if (response.ok) {
          const data = await response.json();
          if (data.products && data.products.length > 0) {
            const products = data.products.map(p => ({
              id: p.id, title: p.title, url: `/products/${p.handle}`,
              image: p.images && p.images.length > 0 ? p.images[0].src : '',
              price: p.variants && p.variants.length > 0 ? p.variants[0].price : '0',
            }));
            this.showInitialResults({ products, queries: [] });
            return;
          }
        }
      }
      this.showInitialResults({ products: [], queries: [] });
    } catch (error) {
      console.error('Erro na busca inicial:', error);
    }
  }

  showInitialResults({ products, queries }) {
    const resultsContainer = this.predictiveSearch.querySelector('.predictive-search-results');
    if (!resultsContainer) return;

    resultsContainer.innerHTML = `
      <div class="predictive-search-products"></div>
      <div class="predictive-search-sidebar">
        <div class="predictive-search-queries"></div>
        <div class="predictive-search-collections"></div>
      </div>
    `;
    resultsContainer.style.display = 'flex';
    
    this.renderInitialQueries(resultsContainer.querySelector('.predictive-search-queries'));
    if (products && products.length > 0) {
      this.renderInitialProducts(products.slice(0, 6), resultsContainer.querySelector('.predictive-search-products'));
    }
    
    this.initProductsSwiper();
    this.checkSidebarContent(resultsContainer);
  }

  renderInitialQueries(container) {
    const suggestionsText = this.predictiveSearch.getAttribute('data-search-suggestions') || 'Lançamentos, Conjuntos, Leggings, Bermudas, Tops, Macacões, Blusas, Acessórios';
    const suggestions = suggestionsText.split(',').map(s => s.trim()).filter(s => s.length > 0);
    if (suggestions.length === 0) return;
    
    const suggestionsHTML = suggestions.map(suggestion => `
      <div class="predictive-search-item predictive-search-query link-holder">
        <button type="button" class="predictive-search-suggestion-btn" data-suggestion="${suggestion}">
          <h4 class="predictive-search-item-title link-default">${suggestion}</h4>
        </button>
      </div>
    `).join('');
    
    container.innerHTML = `
      <div class="predictive-search-section">
        <h4 class="predictive-search-section-title">Sugestões</h4>
        <div class="predictive-search-items">${suggestionsHTML}</div>
      </div>
    `;
    this.bindSuggestionButtons(container);
  }

  renderInitialProducts(products, container) {
    const productsHTML = products.map(product => {
      const productHandle = product.url.split('/').pop().split('?')[0];
      const imageId = `search-img-${productHandle}-${Math.floor(Math.random() * 1000000)}`;
      setTimeout(() => this.fetchProductImage(productHandle, imageId), 100);
      return `
      <div class="swiper-slide">
        <div class="predictive-search-product-card">
          <a href="${product.url}" class="predictive-search-product-link link-holder">
            <div class="predictive-search-product-image">
               <img id="${imageId}" src="${product.image}" loading="lazy" style="opacity: 0.5;">
            </div>
            <div class="predictive-search-product-info">
              <h4 class="predictive-search-product-title link-default">${product.title}</h4>
              <div class="predictive-search-product-prices">
                <span class="predictive-search-regular-price">R$ ${parseFloat(product.price).toFixed(2).replace('.', ',')}</span>
              </div>
            </div>
          </a>
        </div>
      </div>`;
    }).join('');
    
    container.innerHTML = `
      <div class="predictive-search-section">
        <h4 class="predictive-search-section-title">Produtos Sugeridos</h4>
        <div class="predictive-search-products-swiper">
          <div class="swiper">
            <div class="swiper-wrapper">${productsHTML}</div>
            <div class="swiper-pagination"></div>
            <div class="swiper-button-prev"></div>
            <div class="swiper-button-next"></div>
          </div>
        </div>
      </div>`;
  }

  async fetchProductImage(productHandle, imageElementId) {
    try {
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) return;
      const product = await response.json();
      if (product.images && product.images.length > 0) {
        const imageElement = document.getElementById(imageElementId);
        if (imageElement) {
          imageElement.src = product.images[0];
          imageElement.style.opacity = '1';
        }
      }
    } catch (e) {}
  }

  bindSuggestionButtons(container) {
    container.querySelectorAll('.predictive-search-suggestion-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const suggestion = btn.getAttribute('data-suggestion');
        if (suggestion && this.searchInput) {
          this.searchInput.value = suggestion;
          this.searchInput.focus();
          this.handleSearchInput(suggestion);
        }
      });
    });
  }

  hidePredictiveSearch() {
    this.predictiveSearch.style.display = 'none';
    this.clearResults();
    if (window.innerWidth <= 768) this.releaseScroll();
  }
  
  clearResults() {
    if (this.productsSwiper) {
      this.productsSwiper.destroy(true, true);
      this.productsSwiper = null;
    }
    const resultsContainer = this.predictiveSearch.querySelector('.predictive-search-results');
    if (resultsContainer) {
      resultsContainer.innerHTML = '';
      resultsContainer.style.display = 'none';
    }
  }

  highlightText(text) {
    if (!this.currentQuery || !text) return text;
    const regex = new RegExp(`(${this.escapeRegex(this.currentQuery)})`, 'gi');
    return text.replace(regex, '<mark class="predictive-search-highlight">$1</mark>');
  }
  
  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  
  initProductsSwiper() {
    if (this.productsSwiper) {
      this.productsSwiper.destroy(true, true);
      this.productsSwiper = null;
    }
    requestAnimationFrame(() => {
      setTimeout(() => {
        const swiperElement = this.predictiveSearch.querySelector('.predictive-search-products-swiper .swiper');
        if (swiperElement && window.Swiper) {
          this.productsSwiper = new Swiper(swiperElement, {
            slidesPerView: 2, spaceBetween: 12,
            pagination: { el: '.swiper-pagination', clickable: true },
            navigation: { nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' },
            breakpoints: {
              500: { slidesPerView: 3, spaceBetween: 16 },
              1200: { slidesPerView: 4, spaceBetween: 16 },
              1400: { slidesPerView: 5, spaceBetween: 16 }
            }
          });
        }
      }, 50);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.predictiveSearchInstance = new PredictiveSearch();
});
